/* eslint-disable no-self-assign */
import { useState } from 'react';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// Material UI imports
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, useTheme } from '@mui/material/styles';
import { green, amber, red, grey as muiGrey } from '@mui/material/colors';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare, faCaretLeft, faCaretRight, faComment, faHouse } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { getTradelinesStatus, getTradelineTimespan, goalToText } from './utils.js';
import { PIPELINE_TRADELINES as PIPELINE } from '../accounts/AccountFunnelPipeline.js';
import PIPELINE_COLORS from '../accounts/AccountFunnelPipeline.colors.js';
import TimelineEditor from '../timeline/Timeline.editor.js';

dayjs.extend(duration)
dayjs.extend(relativeTime)

function TradelineRow({ tradeline, opportunities, exchangeRates, onOpen, accounts, tradelines, emails, onSendUpdate, organization }) {
    const [ timespanJump, setTimespanJump ] = useState(0);
    const [ showAddComment, setShowAddComment ] = useState(false);
    const [ sendingComment, setSendingComment ] = useState(false);
    const [ sendCommentSuccessful, setSendCommentSuccessful ] = useState(false);
    const [ sendCommentError, setSendCommentError ] = useState(false);
    const edited = tradeline.modified;
    const theme = useTheme();

    function _setShowAddComment(val) {
        setShowAddComment(val);
        setSendCommentError(false);
        setSendCommentSuccessful(false);
    }

    const { aggregated, results } = getTradelinesStatus({ opportunities, exchangeRates, tradeline, timespanJump, organization })
    const timespan = getTradelineTimespan({ tradeline, timespanJump })

    const sumsText = _.map(tradeline.goals, g => {
        const text = goalToText({ goal: g, transportationMethod: tradeline.transportationMethod, html: true });
        const result = results[g.id];
        if (result) {
            return `${text} (${Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0 }).format(result.progress)})`;
        }
        return text;
    }).join(' | ');

    async function onUpdateDone({ html, tags, files, prevCreated }) {
        setSendingComment(true);
        setSendCommentError(false);
        setSendCommentSuccessful(false);
        try {
            await onSendUpdate({ html, tags, files, prevCreated });
            _setShowAddComment(false);
            setSendingComment(false);
            setSendCommentSuccessful(true);
            await new Promise(resolve => setTimeout(resolve, 3000));
            setSendCommentSuccessful(false);
        } catch (error) {
            setSendingComment(false);
            setSendCommentError(true);
            console.error(error);
        }
    }

    return (
        <Paper sx={{ marginTop: '24px', border: `1px solid ${theme.palette.border1}`, background: theme.palette.panel1, width: '100%' }} fullWidth elevation={0} >
            <CardHeader
                tradeline={tradeline}
                opportunities={opportunities}
                exchangeRates={exchangeRates}
                onOpen={onOpen}
                accounts={accounts}
                timespanJump={timespanJump}
                setTimespanJump={setTimespanJump}
                aggregated={aggregated}
                timespan={timespan}
                showAddComment={showAddComment}
                setShowAddComment={_setShowAddComment}
                sendingComment={sendingComment}
            />
            <Box sx={{ padding: '12px 16px' }}>
                {/* Goals progress bars */}
                {tradeline.goals?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                        {tradeline.goals.map(g => {
                            const r = results[g.id];
                            const progress = Math.round((r?.progress || 0) * 100);
                            const barColor = progress >= 100 ? green[500] : progress >= 50 ? amber[500] : red[400];
                            const fraction = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
                            const showCurrency = (g.type === 'revenue' || g.type === 'profit') && g.currency;
                            const fmtVal = (v) => showCurrency ? `${g.currency} ${fraction.format(v)}` : fraction.format(v);
                            return (
                                <Box key={g.id} sx={{ flex: 1, minWidth: '160px' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                            {goalToText({ goal: g, transportationMethod: tradeline.transportationMethod, html: false }).split(':')[0]}
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: progress >= 100 ? green[700] : '#334155' }}>
                                            {fmtVal(r?.value || 0)} / {fmtVal(r?.goal || g.value)} ({progress}%)
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant='determinate'
                                        value={Math.min(100, progress)}
                                        sx={{
                                            height: 6, borderRadius: 3, backgroundColor: muiGrey[200],
                                            '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 3 },
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                )}
                {/* Meta info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant='caption' sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                        Owner: <b>{(tradeline.owner || '').split('@')[0]}</b>
                    </Typography>
                    <Typography variant='caption' sx={{ color: muiGrey[300] }}>|</Typography>
                    <Typography variant='caption' sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                        Created <b>{dayjs.duration((tradeline.created - new Date().getTime()) / 1000, 'seconds').humanize(true)}</b>
                    </Typography>
                    {edited && (
                        <>
                            <Typography variant='caption' sx={{ color: muiGrey[300] }}>|</Typography>
                            <Typography variant='caption' sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                Edited <b>{dayjs.duration((tradeline.modified - new Date().getTime()) / 1000, 'seconds').humanize(true)}</b>
                            </Typography>
                        </>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button variant='text' size='small' onClick={() => onOpen({ tradeline, newWindow: true })} sx={{ textTransform: 'none', fontSize: '11px', color: theme.palette.text.secondary }}>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '10px' }} />&nbsp;Open
                    </Button>
                </Box>
            </Box>
            {(tradeline.tags || []).length > 0 && (
                <Box sx={{ padding: '6px' }} >
                    {tradeline.tags.map(tag => (<Chip sx={{ marginRight: '6px' }} label={tag.label} key={tag.label} />))}
                </Box>
            )}
            {showAddComment && (
                <Box sx={{ p: 1, m: 2, mt: 1, mb: 1, border: `1px solid ${theme.palette.border1}`, position: 'relative' }}>
                    <TimelineEditor
                        organization={organization}
                        edit={true}
                        initialUpdate={{}}
                        onEdit={() => {}}
                        onUpdateDone={({ html, tags, files, prevCreated }) => onUpdateDone({ html, tags, files, prevCreated })}
                        onClose={() => {}}
                        possibleUpdateTags={_.compact(tradelines?.map(t => t.updates).flat().map(t => t?.tags).flat())}
                        featureToggle={{}}
                        opportunities={[]}
                        tradelines={[]}
                        noPadding={true}
                    />
                    {sendingComment && (
                        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: alpha(theme.palette.background.default, 0.8) }}>
                        </Box>
                    )}
                </Box>
            )}
            {sendCommentSuccessful && (
                <Alert severity='success'>Comment sent successfully</Alert>
            )}
            {sendCommentError && (
                <Alert severity='error'>Error sending comment. Please try again.</Alert>
            )}
        </Paper>
    );
}

export default TradelineRow;
export { CardHeader, DescriptionToNode };

function CardHeader(props) {
    const { tradeline, opportunities, exchangeRates, onOpen, timespanJump, 
        setTimespanJump, aggregated, timespan, showAddComment, setShowAddComment, sendingComment } = props;
    const theme = useTheme();

    const status = aggregated?.status;

    const pipelineStep = (tradeline.tags || []).find(t => t.label.startsWith('Pipeline: ')) || { label: PIPELINE[0] };
    const pipelineStepIndex = PIPELINE.indexOf(pipelineStep.label.replace('Pipeline: ', ''));

    return (
        <Box sx={{ display: 'flex', padding: '6px', alignItems: 'center', borderBottom: `1px solid ${theme.palette.border1}` }}>
            <Box sx={{ width: 'auto', paddingRight: '12px' }}>
                {(!status || status === 'inprogress') && (
                    <Chip size='small' label='In Progress' color='chip-request-new' />
                )}
                {status === 'completed' && (
                    <Chip size='small' label='Completed' color='chip-won' />
                )}
                {status === 'failed' && (
                    <Chip size='small' label='Passed' color='chip-lost' />
                )}
            </Box>
            <Box sx={{ width: 'auto', paddingRight: '12px' }}>
                <Chip label={pipelineStep.label.replace('Pipeline: ', '')} size='small' sx={{ fontSize: '11px', fontWeight: 600, color: '#fff', backgroundColor: PIPELINE_COLORS[pipelineStepIndex] || '#64748b' }} />
            </Box>
            <Box>
                <Button variant='text' color='primary' sx={{ color: '#2185D0', textDecoration: 'underline' }} onClick={() => onOpen({ tradeline })}>
                    <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700, textAlign: 'left' }}>
                        {tradeline.title}
                    </Typography>
                </Button>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ width: '250px', textAlign: 'end' }}>
                <IconButton
                    onClick={() => setTimespanJump(timespanJump - 1)}
                    sx={{ visibility: tradeline.timespanType === 'recurring' ? 'visible' : 'hidden' }}
                >
                    <FontAwesomeIcon icon={faCaretLeft} />
                </IconButton>
                <Typography variant='caption'>
                    {dayjs(timespan.start).format('L')} - {dayjs(timespan.end).format('L')}
                </Typography>
                <IconButton
                    onClick={() => setTimespanJump(timespanJump + 1)}
                    sx={{ visibility: tradeline.timespanType === 'recurring' ? 'visible' : 'hidden' }}
                >
                    <FontAwesomeIcon icon={faCaretRight} />
                </IconButton>
            </Box>
            <Box sx={{ borderLeft: `1px solid ${theme.palette.border1}`}} >
                <Button sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: theme.palette.text.secondary }} onClick={() => onOpen({ tradeline })}>
                    &nbsp;
                    <FontAwesomeIcon icon={faHouse} />
                    &nbsp;
                    &nbsp;
                    <Typography variant='caption'>
                        Details
                    </Typography>
                    &nbsp;
                </Button>
            </Box>
            <Box sx={{ borderLeft: `1px solid ${theme.palette.border1}`}} >
                <Button disabled={sendingComment} sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: showAddComment ? theme.palette.secondary.main : theme.palette.text.secondary }} onClick={() => setShowAddComment(!showAddComment)}>
                    &nbsp;
                    <FontAwesomeIcon icon={faComment} />
                    &nbsp;
                    &nbsp;
                    <Typography variant='caption'>
                        {sendingComment ? <CircularProgress size={12} /> : (showAddComment ? 'Cancel' : 'Add Comment')}
                    </Typography>
                    &nbsp;
                </Button>
            </Box>
        </Box>
    );
}

function DescriptionToNode({ tradeline }) {
    return tradeline.description;
}